'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { products } from '@/data/products';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function ShoppingCartPage() {
  const params = useParams();
  const id = Number(params?.id);
  const product = products?.find((p) => p?.id === id) || products?.[0];
  const [qty, setQty] = useState(1);

  const mrp = product?.originalPrice;
  const discount = mrp - product?.price;
  const total = product?.price * qty;
  const totalMrp = mrp * qty;
  const totalDiscount = discount * qty;

  return (
    <div>
      <Navbar />
      <div className="pt-14 md:pt-16 min-h-screen bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
          {/* Back */}
          <Link href="/" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0c831f] transition-colors mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Continue shopping
          </Link>

          {/* Delivery Banner */}
          <div className="bg-[#0c831f] rounded-2xl p-4 flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <div>
                <p className="text-white font-bold text-sm">Delivery in 4 hours</p>
                <p className="text-white/70 text-xs">Fastest delivery in your area</p>
              </div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white/60">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Cart Items */}
            <div className="md:col-span-2 flex flex-col gap-4">
              {/* Cart Header */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-extrabold text-gray-900">
                      My Cart <span className="text-sm font-semibold text-gray-500 ml-1">{qty} item</span>
                    </h2>
                    <span className="text-xs text-green-700 font-bold bg-green-50 px-2 py-0.5 rounded">₹{totalDiscount?.toLocaleString()} saved</span>
                  </div>
                </div>

                {/* Cart Item */}
                <div className="flex items-start gap-4 border-t border-gray-100 pt-4">
                  <img
                    alt={product?.name}
                    src={product?.image}
                    className="w-20 h-20 object-contain rounded-xl border border-gray-100 bg-gray-50 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 line-clamp-2">{product?.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{qty} unit</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-base font-extrabold text-gray-900">₹{product?.price}</span>
                      <span className="text-xs text-gray-400 line-through">₹{product?.originalPrice?.toLocaleString()}</span>
                      <span className="text-[10px] text-green-700 font-bold bg-green-50 px-1.5 py-0.5 rounded">{product?.discount}% Off</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2 border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                        className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14" />
                        </svg>
                      </button>
                      <span className="w-8 text-center text-sm font-bold text-gray-800">{qty}</span>
                      <button
                        onClick={() => setQty((q) => q + 1)}
                        className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 5v14" />
                          <path d="M5 12h14" />
                        </svg>
                      </button>
                    </div>
                    <button className="text-red-400 hover:text-red-600 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Delivery Tag */}
                <div className="mt-4 flex items-center gap-2 text-xs text-[#0c831f] font-semibold">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="fill-[#0c831f]">
                    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
                  </svg>
                  Delivery in <span className="font-extrabold">4 hours</span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="bg-white rounded-2xl border border-gray-200 p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { icon: '✅', label: '100% Genuine' },
                  { icon: '🚚', label: 'Free Delivery' },
                  { icon: '🔒', label: 'Secure Checkout' },
                  { icon: '👥', label: '1M+ Customers' },
                ]?.map((badge) => (
                  <div key={badge?.label} className="flex items-center gap-2">
                    <span className="text-lg">{badge?.icon}</span>
                    <span className="text-xs font-semibold text-gray-700">{badge?.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Bill Summary */}
            <div className="flex flex-col gap-4">
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <h3 className="text-base font-extrabold text-gray-900 mb-4">Bill details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">MRP ({qty} item)</span>
                    <span className="font-semibold">₹{totalMrp?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Product discount</span>
                    <span className="font-semibold text-green-600">− ₹{totalDiscount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Delivery fee</span>
                    <span className="font-semibold text-green-600">Free</span>
                  </div>
                  <div className="border-t border-gray-100 pt-3 flex justify-between">
                    <span className="font-extrabold text-gray-900">To pay</span>
                    <span className="font-extrabold text-gray-900">₹{total?.toLocaleString()}</span>
                  </div>
                </div>
                <div className="mt-3 bg-green-50 rounded-xl px-3 py-2 text-xs text-green-700 font-semibold text-center">
                  🎉 You are saving ₹{totalDiscount?.toLocaleString()} on this order!
                </div>
              </div>

              {/* Checkout Button */}
              <Link href={`/checkout/${product?.id}`}>
                <button className="w-full flex items-center justify-between px-5 py-4 rounded-2xl bg-[#0c831f] text-white font-extrabold text-sm hover:bg-[#065f12] transition-colors active:scale-95 shadow-lg">
                  <span>Proceed to checkout</span>
                  <div className="flex items-center gap-2">
                    <span>₹{total?.toLocaleString()}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m9 18 6-6-6-6" />
                    </svg>
                  </div>
                </button>
              </Link>
              <p className="text-xs text-gray-400 text-center">🔒 Secured by SSL • UPI • Cards • Net Banking</p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
